import {
  createRoute,
  notFound,
  Outlet,
  retainSearchParams,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { BookingRecordScreen } from "@/app/Bookings/Screens/BookingRecordScreen";
import { ModelType } from "@/gql/graphql";
import {
  fetchQuery,
  prefetchInfiniteQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { BookingCreateScreen } from "@/app/Bookings/Screens/BookingCreateScreen";
import { BookingDetailsScreen } from "@/app/Bookings/Screens/BookingDetailsScreen";
import { BookingFindRelocationScreen } from "@/app/Bookings/Screens/BookingFindRelocationScreen";
import { BookingChangeRequestScreen } from "@/app/Bookings/Screens/BookingChangeRequestScreen";
import { BookingPaymentScreen } from "@/app/Bookings/Screens/BookingPaymentScreen";
import { BookingTaskScreen } from "@/app/Bookings/Screens/BookingTaskScreen";
import { BookingReceiptsScreen } from "@/app/Bookings/Screens/BookingReceiptsScreen";
import { bookingRecordQuery } from "@/app/Bookings/GraphQL/bookingRecordQuery";
import { BookingTableScreen } from "@/app/Bookings/Screens/BookingTableScreen";
import { getBookingListQueryVariables } from "../Utils/getBookingListQueryVariables";
import { bookingListQuery } from "../GraphQL/bookingListQuery";
import { BookingListScreen } from "@/app/Bookings/Screens/BookingListScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import { createQueryVariables } from "@/lib/Components/DataTable/utils/data-table-utils";
import {
  bookingTableDefaultSorting,
  getBookingTableVariables,
} from "@/app/Dashboard/Utils/getBookingTableVariables";
import { activityLogListQuery } from "@/app/ActivityLogs/GraphQL/activityLogListQuery";
import { getActivityLogFeedQueryVariables } from "@/app/ActivityLogs/Utils/getActivityLogFeedQueryVariables";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";
import { isRecordId } from "@/routes/recordId";
import {
  bookingTableSearchKeys,
  bookingTableSearchParams,
  bookingTableStateFromSearch,
  type BookingTableSearchParams,
} from "@/app/Bookings/Utils/bookingTableSearchParams";

export type BookingListSearchParams = BookingTableSearchParams;

export const bookingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/bookings",
  component: Outlet,
  validateSearch: zodValidator(bookingTableSearchParams),
  search: {
    middlewares: [retainSearchParams(bookingTableSearchKeys)],
  },
});

export const bookingTableRoute = createRoute({
  getParentRoute: () => bookingsRoute,
  path: "/",
  head: () => ({
    meta: [
      {
        title: "Bookings - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage and view all bookings",
      },
    ],
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const state = bookingTableStateFromSearch(deps);
    const queryVariables = createQueryVariables(
      "bookings",
      getBookingTableVariables({
        supplierId: supplierParamToId(params.supplierId) ?? state.supplierId,
        status: state.statuses,
        deliveryCityIds: state.deliveryCityIds,
        departureCityIds: state.departureCityIds,
        vehicles: state.vehicles,
        relocationTypes: state.relocationTypes,
        hasMatches: state.hasMatches,
      }),
      bookingTableDefaultSorting,
      { page: state.page, search: state.search },
    );

    return fetchQuery(bookingListQuery, queryVariables, params.orgId);
  },
  component: BookingTableScreen,
});

export const bookingRecordLayout = createRoute({
  getParentRoute: () => bookingsRoute,
  id: "_bookingRecordLayout",
  component: BookingListScreen,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    const variables = getBookingListQueryVariables(deps, supplierId);
    return prefetchInfiniteQuery(bookingListQuery, variables, params.orgId);
  },
});

export const bookingCreateRoute = createRoute({
  getParentRoute: () => bookingRecordLayout,
  path: "/create",
  component: BookingCreateScreen,
  head: () => ({
    meta: [
      {
        title: "Create Booking - Imoova Admin",
      },
    ],
  }),
});

export const bookingRoute = createRoute({
  getParentRoute: () => bookingRecordLayout,
  path: "/$bookingId",
  component: BookingRecordScreen,
  beforeLoad: ({ params }) => {
    if (!isRecordId(params.bookingId)) {
      throw notFound();
    }
  },
  loader: ({ params }) => {
    return Promise.all([
      fetchQuery(bookingRecordQuery, { id: params.bookingId }, params.orgId),
    ]);
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.at(0)?.booking.reference} - Imoova Admin`,
      },
    ],
  }),
});

export const bookingDetailRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/",
  component: BookingDetailsScreen,
  loader: ({ params }) =>
    prefetchInfiniteQuery(
      activityLogListQuery,
      getActivityLogFeedQueryVariables(params.bookingId, ModelType.Booking),
      params.orgId,
    ),
});

export const bookingFindRelocationRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/find-relocation",
  component: BookingFindRelocationScreen,
});

export const bookingChangeRequestsRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/change-requests",
  component: BookingChangeRequestScreen,
});

export const bookingPaymentsRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/payments",
  component: BookingPaymentScreen,
});

export const bookingTasksRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/tasks",
  component: BookingTaskScreen,
});

export const bookingReceiptsRoute = createRoute({
  getParentRoute: () => bookingRoute,
  path: "/receipts",
  component: BookingReceiptsScreen,
});
